import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/AddConditionModal.module.css';
import battlefield from '../styles/Battlefield.module.css';
import useFocusTrap from '../hooks/useFocusTrap.js';
import NumberInput from './NumberInput';
import BattlefieldModal from './BattlefieldModal';
import { surfaceTraitGroups, traitOnValue } from '../engine/battlefield/surfaceTraits';

/** Is a trait switched on? A number counts when above zero. */
const isOnValue = (v) => (typeof v === 'number' ? v > 0 : !!v);

/**
 * One trait: its switch, and - for a numeric trait that is on - its value.
 * A numeric trait is only half-answered by being on: Regeneration 1 and
 * Regeneration 5 are different ground. The field appears once it is
 * switched on, and never before - it would be noise otherwise.
 */
function TraitCell({ trait, value, onToggle }) {
  const on = isOnValue(value);
  return (
    <div className={battlefield.traitCell}>
      <button
        type="button"
        aria-pressed={on}
        aria-label={`Grants ${trait.label}`}
        data-testid={`trait-${trait.field}`}
        title={trait.hint}
        className={clsx(battlefield.brush, on && battlefield.brushActive)}
        onClick={() => onToggle(trait.field, on ? false : traitOnValue(trait))}
      >
        {trait.label}
      </button>
      {trait.numeric && on && (
        <NumberInput
          className={common.input}
          aria-label={`${trait.label} value`}
          min={1}
          max={20}
          value={value ?? trait.preset ?? 1}
          onChange={(v) => onToggle(trait.field, Math.max(1, Math.min(20, v ?? 1)))}
        />
      )}
    </div>
  );
}

/**
 * SurfaceTraitModal - what the ground lends you while you stand on it.
 *
 * A sibling of `AddConditionModal` rather than a copy of it: conditions
 * are picked from a catalogue, traits are a grid of switches. Every one
 * of them writes a real `Target` field the engine already reads - the
 * catalogue is in `engine/battlefield/surfaceTraits.ts` and a test pins
 * that, because a trait nothing reads would sit on the sheet looking
 * authoritative and changing no roll at all.
 *
 * BUTTONS, NOT CHECKBOXES, and laid out in columns: a checklist of
 * twenty makes a GM read every line to find one. A grid is scanned. The
 * hint is on hover, where it does not cost a line of its own.
 */
export default function SurfaceTraitModal({ traits, onToggle, onClose }) {
  const trapRef = useFocusTrap(true);
  return (
    <BattlefieldModal
      onBackdropClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      trapRef={trapRef}
      className={clsx(common.modalContent, styles.content)}
      label="Traits this ground lends"
      testId="surface-traits"
    >
      <div className={styles.header}>
        <h3 className={styles.title}>Traits this ground lends</h3>
        <button type="button" className={styles.closeBtn} aria-label="Close" onClick={onClose}>
          &times;
        </button>
      </div>
      {surfaceTraitGroups().map((group) => (
        <div key={group.id} className={styles.categorySection}>
          <div className={styles.categoryHeader}>{group.title}</div>
          <div className={battlefield.traitGrid}>
            {group.traits.map((trait) => (
              <TraitCell key={trait.field} trait={trait} value={traits?.[trait.field]} onToggle={onToggle} />
            ))}
          </div>
        </div>
      ))}
      <div className={styles.categoryHeader}>
        A surface never exempts you from itself: what it lends works on all other ground.
      </div>
    </BattlefieldModal>
  );
}
